import { useEffect, useId, useRef, useState } from "react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "@/lib/api/client";
import type { components } from "@/lib/api/schema.gen";
import { unwrap } from "@/lib/api/unwrap";
import { cn } from "@/lib/utils/cn";

import { Input } from "./input";

export type AddressSuggestion = components["schemas"]["AddressSuggestion"];
export type AddressResolution = components["schemas"]["AddressResolveResponse"];

/**
 * An address field that suggests Canadian civic addresses while typing.
 *
 * A plain text field first: the value is whatever the user typed, and choosing
 * a suggestion is optional. Choosing one asks the server to confirm it and hands
 * the confirmed parts to `onResolved` — the caller decides which fields they
 * fill (an enterprise fills street, city and postal code; an expert's location
 * its address, région and MRC).
 *
 * Mirrors the Django app's `address-autocomplete.js`: same thresholds, same
 * session handling, same wording. When the service says it cannot work at all
 * (switched off, no key, refused key), the field stops asking and stays plain
 * for as long as it is mounted. A request that fails once (throttled, a Google
 * hiccup) only hides the list, and the next pause asks again. Never an error
 * message.
 */

/** Fewer characters match too much to be worth a request. */
export const MIN_QUERY_LENGTH = 3;
/** A pause in typing, not every keystroke. */
export const DEBOUNCE_MS = 250;

const UNCONFIRMED: AddressResolution = {
  confirmed: false,
  address: "",
  city: "",
  zip_code: "",
  province: "",
  formatted_address: "",
  mrc: "",
  region: "",
  region_id: null,
};

/** A UUID v4. `crypto.randomUUID` exists only in secure contexts, and a local
 * domain over plain http is not one; `getRandomValues` has no such limit. */
export function newSessionToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
    "",
  );
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
}

type InputProps = Omit<
  React.ComponentProps<"input">,
  "value" | "onChange" | "type" | "role" | "autoComplete"
>;

export function AddressAutocomplete({
  value,
  onChange,
  onResolved,
  className,
  ...inputProps
}: InputProps & {
  value: string;
  onChange: (value: string) => void;
  /**
   * Called once a chosen suggestion has been confirmed — or not: when
   * `result.confirmed` is false every part is empty, and the caller keeps
   * `suggestion.text` as the address.
   */
  onResolved: (
    result: AddressResolution,
    suggestion: AddressSuggestion,
  ) => void;
}) {
  const { t, i18n } = useTranslation();
  const listId = useId();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [unavailable, setUnavailable] = useState(false);
  const session = useRef<string | null>(null);
  // The same token, as state, so it can key the cache: a list cached under an
  // earlier session would let a choice end a session that never searched.
  const [sessionKey, setSessionKey] = useState<string | null>(null);
  // Bumped by every keystroke: a resolution that returns after the user typed
  // on must not overwrite what they typed.
  const choice = useRef(0);
  // The caller's latest callback, not the one captured when the address was
  // chosen: a resolution can take seconds, and a caller that computes from its
  // own state (a list of places) must not write back the state of that moment.
  const onResolvedRef = useRef(onResolved);
  useEffect(() => {
    onResolvedRef.current = onResolved;
  });

  // Only what the user types searches: a value loaded from the profile, or one
  // a resolution just wrote, must not pop a list open.
  const typed = useRef(false);

  useEffect(() => {
    if (!typed.current) return;
    // Too short to search: drop the list now, as the Django field does, rather
    // than leave a stale one up for the length of the pause.
    if (value.trim().length < MIN_QUERY_LENGTH) {
      setQuery("");
      return;
    }
    const timer = window.setTimeout(() => setQuery(value.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [value]);

  const enabled = !unavailable && query.length >= MIN_QUERY_LENGTH;
  const suggestions = useQuery({
    // The server answers in the request's language. No staleTime: a fetch,
    // not a cache hit, is what opens the Google session the choice then ends.
    queryKey: ["addresses", "suggest", i18n.language, sessionKey, query],
    enabled,
    retry: false,
    // The previous list stays up while the next one loads, instead of blinking.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      session.current ??= newSessionToken();
      return unwrap(
        api.GET("/api/v1/addresses/suggest/", {
          params: { query: { q: query, session: session.current } },
        }),
      );
    },
  });

  // Suggestions cannot work at all here: stop asking. A failed request (a 503,
  // a 429, the network) only hides this list; the next pause asks again.
  useEffect(() => {
    if (suggestions.data?.available === false) setUnavailable(true);
  }, [suggestions.data]);

  // A new list starts with nothing highlighted.
  useEffect(() => setActive(-1), [suggestions.data]);

  const resolve = useMutation({
    mutationFn: async ({
      suggestion,
      token,
    }: {
      suggestion: AddressSuggestion;
      token: string;
    }) =>
      unwrap(
        api.POST("/api/v1/addresses/resolve/", {
          body: { text: suggestion.text, session: token },
        }),
      ),
  });

  const items = enabled ? (suggestions.data?.suggestions ?? []) : [];
  const showList =
    open && enabled && !suggestions.isError && suggestions.data !== undefined;

  const choose = (suggestion: AddressSuggestion) => {
    typed.current = false;
    setOpen(false);
    setQuery("");
    const mine = ++choice.current;
    // The resolution ends Google's billing session: take its token now and
    // let the next search open a new one. Clearing it once the resolution
    // settled instead would wipe a session the user opened by typing while a
    // slow one was still on its way.
    const token = session.current ?? "";
    session.current = null;
    resolve.mutate(
      { suggestion, token },
      {
        onSuccess: (result) => {
          if (mine === choice.current)
            onResolvedRef.current(result, suggestion);
        },
        onError: () => {
          if (mine === choice.current)
            onResolvedRef.current(UNCONFIRMED, suggestion);
        },
      },
    );
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showList) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (items.length ? (i + 1) % items.length : -1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) =>
        items.length ? (i <= 0 ? items.length - 1 : i - 1) : -1,
      );
    } else if (e.key === "Enter" && active >= 0 && items[active]) {
      // Choosing, not submitting the form around the field.
      e.preventDefault();
      choose(items[active]);
    } else if (e.key === "Escape") {
      // Close the list only; a Modal or Sheet around the field stays open.
      e.stopPropagation();
      setOpen(false);
    }
  };

  const optionId = (i: number) => `${listId}-option-${i}`;

  return (
    <div className="relative w-full">
      <Input
        {...inputProps}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={
          showList && active >= 0 ? optionId(active) : undefined
        }
        aria-busy={resolve.isPending || undefined}
        className={className}
        value={value}
        onChange={(e) => {
          typed.current = true;
          // A new Google session opens with the first keystroke after a choice.
          if (!session.current) {
            session.current = newSessionToken();
            setSessionKey(session.current);
          }
          choice.current += 1;
          setOpen(true);
          onChange(e.target.value);
        }}
        onKeyDown={onKeyDown}
        onBlur={(e) => {
          setOpen(false);
          inputProps.onBlur?.(e);
        }}
      />
      {/* Says how many addresses came back, for whoever cannot see the list. */}
      <p className="sr-only" aria-live="polite">
        {showList
          ? items.length
            ? t("common.addressAutocomplete.count", { count: items.length })
            : t("common.addressAutocomplete.noResults")
          : ""}
      </p>
      {showList && (
        <div className="absolute left-0 z-20 mt-1 w-full rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg">
          <ul
            id={listId}
            role="listbox"
            aria-label={t("common.addressAutocomplete.listLabel")}
          >
            {items.length === 0 ? (
              <li className="px-2 py-2 text-sm text-muted-foreground">
                {t("common.addressAutocomplete.noResults")}
              </li>
            ) : (
              items.map((item, i) => (
                <li
                  key={item.place_id || item.text}
                  id={optionId(i)}
                  role="option"
                  aria-selected={i === active}
                  // Before the input's blur closes the list.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(item);
                  }}
                  onMouseEnter={() => setActive(i)}
                  className={cn(
                    "cursor-pointer rounded px-2 py-1.5 text-sm",
                    i === active && "bg-accent text-accent-foreground",
                  )}
                >
                  <span className="font-medium">{item.main_text}</span>
                  {item.secondary_text && (
                    <span className="ml-1 text-muted-foreground">
                      {item.secondary_text}
                    </span>
                  )}
                </li>
              ))
            )}
          </ul>
          {/* Google's attribution rules for predictions shown without a map:
              the words "Google Maps", never translated or wrapped, in Roboto
              (sans-serif fallback) at weight 400, 12px or more, in #1F1F1F,
              #5E5E5E or white. Google fixes the colour, so this is the one
              deliberate exception to "no hex": #5E5E5E as the Django field
              uses, white in the dark theme. */}
          <p
            translate="no"
            className="whitespace-nowrap px-2 pt-1 text-right font-[Roboto,sans-serif] text-xs font-normal text-[#5e5e5e] dark:text-white"
          >
            {t("common.addressAutocomplete.attribution")}
          </p>
        </div>
      )}
    </div>
  );
}
